import axios from "axios";

const AUTH_BASE_URL = process.env.REACT_APP_AUTH_SERVICE_BASE_URL;
const CARS_BASE_URL = process.env.REACT_APP_CARS_SERVICE_BASE_URL;
const ORDERS_BASE_URL = process.env.REACT_APP_ORDERS_SERVICE_BASE_URL;
const WEBSOCKET_BASE_URL = process.env.REACT_APP_WEBSOCKET_SERVICE_BASE_URL || process.env.REACT_APP_WEB_SOCKET_SERVICE_BASE_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Partner Agent APIs
export interface PartnerAgent {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  permissions: {
    canViewTransactions: boolean;
    canViewCars: boolean;
    canViewDrivers: boolean;
    canViewOwners: boolean;
    canViewMap: boolean;
  };
}

export interface CreatePartnerAgentDto {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  canViewTransactions?: boolean;
  canViewCars?: boolean;
  canViewDrivers?: boolean;
  canViewOwners?: boolean;
  canViewMap?: boolean;
}

export interface UpdatePartnerAgentDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  status?: string;
  canViewTransactions?: boolean;
  canViewCars?: boolean;
  canViewDrivers?: boolean;
  canViewOwners?: boolean;
  canViewMap?: boolean;
}

// Owner APIs
export interface Owner {
  _id: string;
  name: string;
  phone: string;
  email?: string;
  address?: string;
  partner_id: string;
}

export interface CreateOwnerDto {
  name: string;
  phone: string;
  email?: string;
  address?: string;
}

// Driver APIs
export interface PartnerDriver {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  isInternalDriver: boolean;
  status?: string;
  vtcStatus?: string;
  isPending?: boolean; // Flag for pending invitations
}

// Transaction/Earnings APIs
export interface PartnerTransaction {
  _id: string;
  driver: {
    driver_id: string;
    driver_name: string;
  };
  car: {
    car_make: string;
    car_model: string;
  };
  pickup_location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  destination_location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  start_time: string;
  end_time: string;
  fare_amount: number;
  partner_commission: number;
  teracar_commission: number;
  ride_status: string;
  ride_type: string;
}

export interface PartnerEarnings {
  total_commission: number;
  total_rides: number;
  total_fare: number;
  period: {
    start_date: string;
    end_date: string;
    filter_type: string;
  };
}

export const partnerDashboardService = {
  // Partner Agents
  getPartnerAgents: async (partnerId: string): Promise<PartnerAgent[]> => {
    const { data } = await axios.get<{ status?: string; data?: PartnerAgent[] } | PartnerAgent[]>(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/agents`,
      { headers: getAuthHeaders() }
    );
    // Handle both wrapped response (status/data) and direct array response
    if (Array.isArray(data)) {
      return data;
    }
    return data.data || [];
  },

  createPartnerAgent: async (partnerId: string, agentData: CreatePartnerAgentDto): Promise<PartnerAgent> => {
    const { data } = await axios.post<{ status: string; data: PartnerAgent }>(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/agents`,
      { ...agentData, partnerId },
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  updatePartnerAgent: async (
    partnerId: string,
    agentId: string,
    agentData: UpdatePartnerAgentDto
  ): Promise<PartnerAgent> => {
    const { data } = await axios.put<{ status: string; data: PartnerAgent }>(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/agents/${agentId}`,
      agentData,
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  deletePartnerAgent: async (partnerId: string, agentId: string): Promise<void> => {
    await axios.post(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/agents/${agentId}/delete`,
      {},
      { headers: getAuthHeaders() }
    );
  },

  // Cars
  getPartnerCars: async (partnerId: string): Promise<any[]> => {
    // Get all drivers for partner first
    const driversResponse = await axios.get<{ status: string; data: PartnerDriver[] }>(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/drivers`,
      { headers: getAuthHeaders() }
    );
    const drivers = Array.isArray(driversResponse.data) 
      ? driversResponse.data 
      : (driversResponse.data.data || []);
    
    const allCars: any[] = [];
    
    // Note: The cars service endpoint requires driver token, so we need a partner-specific endpoint
    // For now, we'll create a simplified version that fetches cars if a partner endpoint exists
    // Otherwise, we'll need to implement a partner cars endpoint in the cars service
    try {
      // Try to fetch cars via partner endpoint (if it exists)
      const response = await axios.get<{ status: string | boolean; data: any[] }>(
        `${CARS_BASE_URL}/api/v1/car/partner/${partnerId}`,
        { headers: getAuthHeaders() }
      );
      
      console.log("Partner cars API response:", response.data);
      
      // Handle both wrapped and direct response formats
      let carsData: any[] = [];
      if (response.data) {
        // Check if it's a wrapped response
        if (typeof response.data === 'object' && 'status' in response.data && 'data' in response.data) {
          const wrappedData = response.data as { status: string | boolean; data: any[] };
          console.log("Wrapped response detected:", wrappedData);
          
          // Backend returns status as boolean (true) or string ("success")
          if ((wrappedData.status === "success" || wrappedData.status === true) && Array.isArray(wrappedData.data)) {
            carsData = wrappedData.data;
            console.log("Extracted cars data:", carsData);
          } else if (Array.isArray(wrappedData.data)) {
            carsData = wrappedData.data;
            console.log("Extracted cars data (no status check):", carsData);
          }
        } else if (Array.isArray(response.data)) {
          // Direct array response
          carsData = response.data;
          console.log("Direct array response:", carsData);
        } else {
          console.warn("Unexpected response format:", response.data);
        }
      }
      
      console.log("Final cars data before mapping:", carsData);
      
      // Map driver names to cars (even if empty array)
      const mappedCars = carsData.map((car: any) => {
        const driver = drivers.find((d: any) => d._id === car.driver_id);
        return {
          ...car,
          driver_name: driver ? `${driver.firstName || ""} ${driver.lastName || ""}`.trim() || "Unknown" : "Unknown",
        };
      });
      
      console.log("Mapped cars:", mappedCars);
      return mappedCars;
    } catch (error: any) {
      // Log the error for debugging
      console.error("Error fetching partner cars:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      // Return empty array instead of throwing
      return [];
    }
  },

  updateCarDriver: async (carId: string, driverId: string): Promise<any> => {
    const { data } = await axios.put<{ status: string; data: any }>(
      `${CARS_BASE_URL}/api/v1/car/driver/update-car-info/${carId}`,
      { driver_id: driverId },
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  updateCar: async (carId: string, carData: any): Promise<any> => {
    const { data } = await axios.put<{ status: string; data: any }>(
      `${CARS_BASE_URL}/api/v1/car/driver/update-car-info/${carId}`,
      carData,
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  updateCarOwner: async (carId: string, ownerId: string | null): Promise<any> => {
    const { data } = await axios.put<{ status: string; data: any }>(
      `${CARS_BASE_URL}/api/v1/car/driver/update-car-info/${carId}`,
      { owner_id: ownerId },
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  createCar: async (carData: any): Promise<any> => {
    const { data } = await axios.post<{ status: string; data: any }>(
      `${CARS_BASE_URL}/api/v1/car/driver/add-car-info`,
      carData,
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  // Owners
  getOwners: async (partnerId: string): Promise<Owner[]> => {
    const { data } = await axios.get<{ status: string; data: { total: number; owners: Owner[] } }>(
      `${CARS_BASE_URL}/api/v1/owner/partner/${partnerId}`,
      { headers: getAuthHeaders() }
    );
    return data.data.owners;
  },

  createOwner: async (ownerData: CreateOwnerDto): Promise<Owner> => {
    const { data } = await axios.post<{ status: string; data: Owner }>(
      `${CARS_BASE_URL}/api/v1/owner/`,
      ownerData,
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  updateOwner: async (ownerId: string, ownerData: Partial<CreateOwnerDto>): Promise<Owner> => {
    const { data } = await axios.put<{ status: string; data: Owner }>(
      `${CARS_BASE_URL}/api/v1/owner/${ownerId}`,
      ownerData,
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  deleteOwner: async (ownerId: string): Promise<void> => {
    await axios.delete(`${CARS_BASE_URL}/api/v1/owner/${ownerId}`, {
      headers: getAuthHeaders(),
    });
  },

  // Drivers
  getPartnerDrivers: async (partnerId: string, isInternal?: boolean): Promise<PartnerDriver[]> => {
    const params = isInternal !== undefined ? { isInternal: isInternal.toString() } : {};
    const { data } = await axios.get<{ status?: string; data?: PartnerDriver[] } | PartnerDriver[]>(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/drivers`,
      { headers: getAuthHeaders(), params }
    );
    // Handle both wrapped response (status/data) and direct array response
    if (Array.isArray(data)) {
      return data;
    }
    return data.data || [];
  },

  addInternalDriver: async (partnerId: string, phone: string): Promise<any> => {
    const { data } = await axios.post<{ status: string; data: any }>(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/drivers/internal`,
      { phone, partnerId },
      { headers: getAuthHeaders() }
    );
    return data.data;
  },

  removeInternalDriver: async (partnerId: string, driverId: string): Promise<void> => {
    await axios.delete(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/drivers/${driverId}`,
      { headers: getAuthHeaders() }
    );
  },

  removePendingInvitation: async (partnerId: string, driverId: string, phone: string): Promise<void> => {
    await axios.delete(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/drivers/pending/${driverId}`,
      { 
        headers: getAuthHeaders(),
        data: { phone }
      }
    );
  },

  getAgencyCode: async (partnerId: string): Promise<string> => {
    const { data } = await axios.get<{ status?: string; data?: { agencyCode: string }; agencyCode?: string }>(
      `${AUTH_BASE_URL}/auth/v1/users/partners/${partnerId}/agency-code`,
      { headers: getAuthHeaders() }
    );
    // Handle both wrapped response (status/data) and direct response
    if (data.agencyCode) {
      // Direct response format: { agencyCode: "..." }
      return data.agencyCode;
    }
    // Wrapped response format: { status: "...", data: { agencyCode: "..." } }
    return data.data?.agencyCode || "";
  },

  // Transactions & Earnings
  getTransactions: async (
    partnerId: string,
    filters: {
      page?: number;
      limit?: number;
      start_date?: string;
      end_date?: string;
      filter_type?: string;
      is_internal?: boolean;
    }
  ): Promise<{ transactions: PartnerTransaction[]; pagination: any }> => {
    const { data } = await axios.get<{
      status: string;
      data: { transactions: PartnerTransaction[]; pagination: any };
    }>(`${ORDERS_BASE_URL}/api/v1/partner/${partnerId}/transactions`, {
      headers: getAuthHeaders(),
      params: filters,
    });
    return data.data;
  },

  getEarnings: async (
    partnerId: string,
    filters: {
      start_date?: string;
      end_date?: string;
      filter_type?: string;
    }
  ): Promise<PartnerEarnings> => {
    const { data } = await axios.get<{ status: string; data: PartnerEarnings }>(
      `${ORDERS_BASE_URL}/api/v1/partner/${partnerId}/earnings`,
      { headers: getAuthHeaders(), params: filters }
    );
    return data.data;
  },

  // Map/WebSocket
  getPartnerDriversMap: async (partnerId: string): Promise<any> => {
    // Try to get websocket base URL from environment variables
    let websocketUrl = process.env.REACT_APP_WEBSOCKET_SERVICE_BASE_URL || 
                       process.env.REACT_APP_WEB_SOCKET_SERVICE_BASE_URL;
    
    if (!websocketUrl) {
      console.error("WebSocket service URL is not configured. Please set REACT_APP_WEBSOCKET_SERVICE_BASE_URL or REACT_APP_WEB_SOCKET_SERVICE_BASE_URL environment variable.");
      throw new Error("WebSocket service URL is not configured");
    }
    
    // Convert WebSocket protocol (wss://) to HTTPS for REST API calls
    websocketUrl = websocketUrl.replace(/^wss:\/\//, 'https://').replace(/^ws:\/\//, 'http://');
    
    // Remove WebSocket-specific paths like /api/ws/service/connect
    websocketUrl = websocketUrl.replace(/\/api\/ws\/service\/connect\/?$/, '');
    websocketUrl = websocketUrl.replace(/\/api\/ws\/?$/, '');
    
    // Ensure the URL doesn't have a trailing slash
    const baseUrl = websocketUrl.replace(/\/$/, "");
    const url = `${baseUrl}/api/v1/partner/${partnerId}/drivers`;
    
    console.log("Fetching partner drivers map from:", url);
    
    // Get API key for websocket service (REST endpoints require API_KEY header, not JWT)
    const apiKey = process.env.REACT_APP_API_KEY || process.env.REACT_APP_WEBSOCKET_API_KEY;
    
    if (!apiKey) {
      console.error("WebSocket API key is not configured. Please set REACT_APP_API_KEY or REACT_APP_WEBSOCKET_API_KEY environment variable.");
      throw new Error("WebSocket API key is not configured");
    }
    
    try {
      const { data } = await axios.get(url, {
        headers: {
          'API_KEY': apiKey,
        },
      });
      
      // Handle both wrapped and direct response formats
      console.log("Raw API response:", data);
      
      if (data && typeof data === 'object') {
        // Check if it's a wrapped response with status and data
        if (data.status && data.data) {
          console.log("Wrapped response detected, returning data.data:", data.data);
          return data.data;
        }
        // Check if data.data exists (nested structure)
        if (data.data) {
          console.log("Nested data structure, returning data.data:", data.data);
          return data.data;
        }
        // Direct response
        console.log("Direct response, returning data:", data);
        return data;
      }
      console.log("Non-object response, returning as-is:", data);
      return data;
    } catch (error: any) {
      console.error("Error fetching partner drivers map:", error);
      console.error("Request URL:", url);
      console.error("Error response:", error.response?.data);
      throw error;
    }
  },

  // Rides
  getPartnerRides: async (
    partnerId: string,
    filters: {
      page?: number;
      limit?: number;
      ride_type?: string;
      ride_status?: string;
      start_date?: string;
      end_date?: string;
      filter_type?: string;
      driver_id?: string;
      car_id?: string;
      owner_id?: string;
    }
  ): Promise<{ rides: any[]; pagination: any }> => {
    const response = await axios.get<{
      status: string | boolean;
      data: { transactions: any[]; pagination: any };
    }>(`${ORDERS_BASE_URL}/api/v1/partner/${partnerId}/transactions`, {
      headers: getAuthHeaders(),
      params: filters,
    });
    
    console.log("Partner rides API response:", JSON.stringify(response.data, null, 2));
    console.log("Response status:", response.status);
    console.log("Response headers:", response.headers);
    
    // Handle both wrapped and direct response formats
    let ridesData: any[] = [];
    let paginationData: any = {};
    
    if (response.data) {
      // Check if it's a wrapped response with status and data
      if (typeof response.data === 'object' && 'status' in response.data && 'data' in response.data) {
        const wrappedData = response.data as { status: string | boolean; data: { transactions: any[]; pagination: any } };
        console.log("Wrapped response detected:", wrappedData);
        console.log("Wrapped status:", wrappedData.status, "Type:", typeof wrappedData.status);
        console.log("Wrapped data:", wrappedData.data);
        
        // Backend returns status as boolean (true) or string ("success")
        if (wrappedData.data) {
          ridesData = wrappedData.data.transactions || [];
          paginationData = wrappedData.data.pagination || {};
          console.log("Extracted rides:", ridesData.length, "pagination:", paginationData);
        } else {
          console.warn("Wrapped response has no data field");
        }
      } else if (typeof response.data === 'object' && 'transactions' in response.data) {
        // Direct response format
        ridesData = (response.data as any).transactions || [];
        paginationData = (response.data as any).pagination || {};
        console.log("Direct response format - rides:", ridesData.length, "pagination:", paginationData);
      } else {
        console.warn("Unexpected response format:", response.data);
        console.warn("Response data keys:", Object.keys(response.data || {}));
      }
    } else {
      console.error("No response data received");
    }
    
    console.log("Final rides data:", ridesData.length, "pagination:", paginationData);
    
    return {
      rides: ridesData,
      pagination: paginationData,
    };
  },

  getPartnerRideDetails: async (
    partnerId: string,
    rideId: string
  ): Promise<any> => {
    const { data } = await axios.get<{
      status: string | boolean;
      data: { ride: any; shared_ride?: any; commission_amount: number; driver_amount: number; premium_driver_fee: number; partner_commission: number };
    }>(`${ORDERS_BASE_URL}/api/v1/partner/${partnerId}/rides/${rideId}`, {
      headers: getAuthHeaders(),
    });
    
    // Handle both wrapped and direct response formats
    if (data && typeof data === 'object' && 'status' in data && 'data' in data) {
      const wrappedData = data as { status: string | boolean; data: { ride: any; shared_ride?: any; commission_amount: number; driver_amount: number; premium_driver_fee: number; partner_commission: number } };
      if (wrappedData.data && wrappedData.data.ride) {
        return wrappedData.data.ride;
      }
    }
    
    throw new Error('Invalid response format');
  },
};

